import { Component, computed, inject, input } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AppConfig } from '../config/app-config';
import { CmsBackgroundComponent } from './cms-background.component';
import {
  CHROME_UPDATE_URL,
  browserDisplayName,
  browserUpdateUrl,
  browserVersion,
  formatMinVersion,
} from '../utils/browser-version-gate';

/** Why this dead-end screen is showing. */
export type UnsupportedBrowserMode = 'unsupported' | 'outdated' | 'installUnsupported';

/**
 * Dead-end screen gated ahead of install:
 *   - `unsupported` — engine cannot run the app (IE, unknown).
 *   - `outdated` — runnable but below the effective version floor.
 *   - `installUnsupported` — Chromium shell outside the install allowlist while
 *     `blockBrowserAccess` is on (Opera etc. — use Chrome/Edge/Brave to install).
 */
@Component({
  selector: 'app-unsupported-browser',
  standalone: true,
  imports: [TranslatePipe, CmsBackgroundComponent],
  template: `
    <app-cms-background
      fallback="login"
      containerClass="flex items-center justify-center px-6 py-12"
    >
      <div class="flex flex-col items-center text-center">
        <div class="mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-white/10">
          <svg
            class="h-7 w-7 text-white/80"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fill-rule="evenodd"
              d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003ZM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75Zm0 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z"
              clip-rule="evenodd"
            />
          </svg>
        </div>

        @if (mode() === 'unsupported') {
          <h1 class="mb-3 text-[28px] font-black leading-9 tracking-[-0.02em] text-white">
            {{ 'common.unsupportedBrowser.title' | translate }}
          </h1>

          <p class="mb-8 max-w-md text-sm leading-relaxed text-white/[0.78]">
            {{ 'common.unsupportedBrowser.body' | translate: { appName } }}
          </p>

          <button
            type="button"
            (click)="openSupportedBrowser()"
            class="inline-flex h-10 min-w-[120px] items-center justify-center rounded-full bg-purple-accent px-7 text-sm font-semibold text-white transition-colors hover:bg-purple-accent/85"
          >
            {{ 'common.unsupportedBrowser.redirect' | translate }}
          </button>
        } @else if (mode() === 'installUnsupported') {
          <h1 class="mb-3 text-[28px] font-black leading-9 tracking-[-0.02em] text-white">
            {{ 'common.installUnsupportedBrowser.title' | translate: { appName } }}
          </h1>

          <p class="mb-8 max-w-md text-sm leading-relaxed text-white/[0.78]">
            {{ 'common.installUnsupportedBrowser.body' | translate: { appName } }}
          </p>

          <button
            type="button"
            (click)="openSupportedBrowser()"
            class="inline-flex h-10 min-w-[120px] items-center justify-center rounded-full bg-purple-accent px-7 text-sm font-semibold text-white transition-colors hover:bg-purple-accent/85"
          >
            {{ 'common.installUnsupportedBrowser.redirect' | translate }}
          </button>
        } @else {
          <h1 class="mb-3 text-[28px] font-black leading-9 tracking-[-0.02em] text-white">
            {{ 'common.unsupportedBrowserVersion.title' | translate }}
          </h1>

          <p class="mb-4 max-w-md text-sm leading-relaxed text-white/[0.78]">
            {{ 'common.unsupportedBrowserVersion.body' | translate: { browserName, appName } }}
          </p>

          <p class="mb-8 text-xs text-white/60">
            @if (currentVersion !== null) {
              {{
                'common.unsupportedBrowserVersion.versions'
                  | translate: { browserName, currentVersion, minVersion: minVersion() }
              }}
            } @else {
              {{
                'common.unsupportedBrowserVersion.versionsUnknown'
                  | translate: { browserName, minVersion: minVersion() }
              }}
            }
          </p>

          <button
            type="button"
            (click)="openUpdatePage()"
            class="inline-flex h-10 min-w-[120px] items-center justify-center rounded-full bg-purple-accent px-7 text-sm font-semibold text-white transition-colors hover:bg-purple-accent/85"
          >
            {{ 'common.unsupportedBrowserVersion.update' | translate }}
          </button>
        }
      </div>
    </app-cms-background>
  `,
})
export class UnsupportedBrowserComponent {
  private readonly translate = inject(TranslateService);

  /** `unsupported` | `outdated` | `installUnsupported` — see type doc. */
  readonly mode = input<UnsupportedBrowserMode>('outdated');
  /** Effective floor for this session (env, optionally raised by CMS). */
  readonly minVersion = input(formatMinVersion());

  readonly appName = AppConfig.JIO_CLOUD_PC_APP_NAME;
  readonly browserName = browserDisplayName();
  readonly currentVersion = browserVersion();
  /** i18n Chrome download URL; falls back to the gate constant if the key is missing. */
  readonly chromeUrl = computed(() => {
    const url = this.translate.instant('common.unsupportedBrowser.chromeUrl');
    return typeof url === 'string' && url.startsWith('http') ? url : CHROME_UPDATE_URL;
  });

  openSupportedBrowser(): void {
    window.open(this.chromeUrl(), '_blank', 'noopener,noreferrer');
  }

  openUpdatePage(): void {
    window.open(browserUpdateUrl(), '_blank', 'noopener,noreferrer');
  }
}
